import {
    useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import {
    Card,
    Typography,
    Divider,
} from "@mui/material";
import { Parser } from 'html-to-react';

import { BillDetailsInterface } from '../../Models/Billing/Billing';

import DateHelper from '../../Utilities/Helpers/DateHelper';
import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';

import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { CurrencyEnum } from '../../Utilities/Enums/CurrencyEnums';
import { DetailsInvoiceField, BillingDetailsField, BillingAddressField, BillingPaymentField } from '../../Utilities/Enums/ParametersEnum';
import { BillingStatusEnum } from '../../Utilities/Enums/StatusEnums';

import FormTextField from '../../CustomViews/Elements/FormTextField';
import FormRichTextEditor from '../../CustomViews/Elements/FormRichTextEditor';

const InvoiceDetailsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none !important
    border-radius: 0px !important;
    box-shadow: none !important;
    width: 100%;
    display: flex;
    justify-content: space-between;

    .amountDetailsContainer {
        width: 30%;
        display: grid;
        grid-template-columns: 1fr;
        gap: 10px;

        .amountDetailsItemContainer {
            width: -webkit-fill-available;
            display: flex;
            justify-content: space-between;

            .detailFieldHeader, .detailFieldvalue {
                font-size: 13px;
                font-weight: 500;
            }

            .detailFieldvalue {
                font-weight: 600;
            }

            .formTextFieldContainer {
                width: 200px;
            }
        }
    }

    .remarksContainer {
        width: 60%;
        grid-template-columns: auto;
        height: max-content;
        gap: 10px;

        .remarksFieldName {
            font-size: 13px;
            font-weight: 500;
            text-decoration: underline;
        }

        .remarksValue, .paymentDetailsItem {
            font-size: 13px !important;

            p, span, label, ul, li, {
                margin: 0px;
                line-height: 1.5;
            }
        }

        .paymentDetailsItem {
            .paymentDetailsTitle {
                color: #878a99 !important;
            }
        }
    }
`;

export default function AmountDetails(props: BillDetailsInterface) {
    // Variables
    let {
        viewType,
        setviewType,
        formData,
        setformData,
        previewData,
        setpreviewData,
        errorMessages,
        setErrorMessages,
        computeBillAmounts,
    } = props;
    let navigate = useNavigate();
    let requiredFields = RequiredParametersHelper.billings();

    // Use States

    //Variables
    let _billData = (viewType == ViewType.PREVIEW ? previewData : formData);
    let currencyCode = (_billData?.[DetailsInvoiceField.CURRENCY_CODE] ?? CurrencyEnum.PHP) != CurrencyEnum.MYR ? (_billData?.[DetailsInvoiceField.CURRENCY_CODE] ?? CurrencyEnum.PHP) : "RM";

    let discountProps = {
        name: DetailsInvoiceField.DISCOUNT_AMOUNT,
        title: 'Discount',
        fieldValue: formData?.[DetailsInvoiceField.DISCOUNT_AMOUNT] ?? 0,
        type: 'number',
        required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.DISCOUNT_AMOUNT)?.keyName != null,
        errorMessages: errorMessages,
        hideLabel: true,
        isRightAlign: true,
        handleOnChange: (selectedValue: any) => {
            if(computeBillAmounts) {
                computeBillAmounts({
                    ...formData,
                    [DetailsInvoiceField.DISCOUNT_AMOUNT]: selectedValue ?? 0,
                });
            }
        },
    },
        remarksProps = {
            name: DetailsInvoiceField.REMARKS,
            title: "Remarks",
            fieldValue: formData?.[DetailsInvoiceField.REMARKS] ?? "",
            required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.REMARKS)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: true,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [DetailsInvoiceField.REMARKS]: selectedValue }));
                }
            }
        };
    //Event Handlers
    
    //Rendering Functions
    const renderPaymentDetails = () => {
        let _paymentData = previewData?.[DetailsInvoiceField.PAYMENT];
        let _transactionId = _paymentData?.[BillingPaymentField.TRANSACTION_ID] != null && _paymentData?.[BillingPaymentField.TRANSACTION_ID] != "" ? _paymentData?.[BillingPaymentField.TRANSACTION_ID] : "--";
        let _currency = _paymentData?.[BillingPaymentField.CURRENCY] ?? previewData?.[DetailsInvoiceField.CURRENCY_CODE];
        let _amount = _currency + GlobalFunctionHelper.amountFormatter(_paymentData?.[BillingPaymentField.AMOUNT_PAID] ?? 0);
        let _cardBrand = (_paymentData?.[BillingPaymentField.CARD_BRAND] != null && _paymentData?.[BillingPaymentField.CARD_BRAND] != "" ? _paymentData?.[BillingPaymentField.CARD_BRAND] : "--").toUpperCase();
        let _cardNo = "**** **** **** " + (_paymentData?.[BillingPaymentField.LAST_4_CARD_DIGIT] != null && _paymentData?.[BillingPaymentField.LAST_4_CARD_DIGIT] != "" ? _paymentData?.[BillingPaymentField.LAST_4_CARD_DIGIT] : "****");
        let _paymentDate = _paymentData?.[BillingPaymentField.PAYMENT_DATE] != null && _paymentData?.[BillingPaymentField.PAYMENT_DATE] != "" ? DateHelper.getFormattedDate(_paymentData?.[BillingPaymentField.PAYMENT_DATE], "dd/MM/yyyy") : "--";
        let _paymentType = (_paymentData?.[BillingPaymentField.PAYMENT_TYPE] != null && _paymentData?.[BillingPaymentField.PAYMENT_TYPE] != "" ? _paymentData?.[BillingPaymentField.PAYMENT_TYPE] : "--").toUpperCase();
        let _provider = (_paymentData?.[BillingPaymentField.PROVIDER] != null && _paymentData?.[BillingPaymentField.PROVIDER] != "" ? _paymentData?.[BillingPaymentField.PROVIDER] : "--").toUpperCase();
        let _remarks = _paymentData?.[BillingPaymentField.REMARKS] != null && _paymentData?.[BillingPaymentField.REMARKS] != "" ? _paymentData?.[BillingPaymentField.REMARKS] : "--";
        
        return <>
            <Typography className="remarksFieldName">Payment Details</Typography>
            <Typography className="paymentDetailsItem"><span className="paymentDetailsTitle">Provider:</span> {_provider}</Typography>
            {_paymentData?.[BillingPaymentField.PAYMENT_TYPE] != "Manual" && <Typography className="paymentDetailsItem"><span className="paymentDetailsTitle">Payment Type:</span> {_paymentType}({_cardBrand}) {_cardNo}</Typography>}
            {_paymentData?.[BillingPaymentField.PAYMENT_TYPE] == "Manual" && <Typography className="paymentDetailsItem"><span className="paymentDetailsTitle">Payment Type:</span> {_paymentType}</Typography>}
            <Typography className="paymentDetailsItem"><span className="paymentDetailsTitle">Reference No:</span> {(_paymentData?.[BillingPaymentField.PAYMENT_TYPE] != "Manual" ? _transactionId : "n/a")}</Typography>
            <Typography className="paymentDetailsItem"><span className="paymentDetailsTitle">Remarks:</span> {_remarks}</Typography>
        </>
    }

    return <InvoiceDetailsContainer className="invoiceDetailsContainer">
        <div className="amountDetailsContainer remarksContainer">
            <Typography className="remarksFieldName">{remarksProps?.title ?? "--"}</Typography>
            {viewType == ViewType.PREVIEW && <div className="remarksValue">{previewData?.[DetailsInvoiceField.REMARKS] ? Parser().parse(previewData?.[DetailsInvoiceField.REMARKS]) : "---"}</div>}
            {viewType != ViewType.PREVIEW && <FormRichTextEditor {...remarksProps} />}

            {viewType == ViewType.PREVIEW && (previewData?.[DetailsInvoiceField.PAYMENT]?.[BillingPaymentField.TRANSACTION_ID] || previewData?.[DetailsInvoiceField.PAYMENT]?.[BillingPaymentField.PAYMENT_TYPE] == 'Manual') && renderPaymentDetails()}
        </div>

        <div className="amountDetailsContainer">
            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">Item Count</Typography>
                <Typography className="detailFieldvalue">1</Typography>
            </div>

            <Divider />

            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">Sub Total</Typography>
                <Typography className="detailFieldvalue">{currencyCode}{GlobalFunctionHelper.amountFormatter(_billData?.[DetailsInvoiceField.SUB_TOTAL] ?? 0, 2)}</Typography>
            </div>

            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">{discountProps?.title ?? "--"}</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="detailFieldvalue">{currencyCode}{GlobalFunctionHelper.amountFormatter(discountProps.fieldValue ?? 0, 2)}</Typography>}
                {viewType != ViewType.PREVIEW && <FormTextField { ...discountProps } />}
            </div>

            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">Tax ({_billData?.[DetailsInvoiceField.TAX_RATE] ?? 0}%)</Typography>
                <Typography className="detailFieldvalue">{currencyCode}{GlobalFunctionHelper.amountFormatter(_billData?.[DetailsInvoiceField.TAX_AMOUNT] ?? 0, 2)}</Typography>
            </div>

            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">Grand Total (Before Rounding)</Typography>
                <Typography className="detailFieldvalue">{currencyCode}{GlobalFunctionHelper.amountFormatter(_billData?.[DetailsInvoiceField.TOTAL_BEFORE_ADJUSTMENT] ?? 0, 2)}</Typography>
            </div>

            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">Rounding Adjust</Typography>
                <Typography className="detailFieldvalue">{currencyCode}{GlobalFunctionHelper.amountFormatter(_billData?.[DetailsInvoiceField.ROUND_ADJUSTMENT] ?? 0, 2)}</Typography>
            </div>

            <Divider />

            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">Grand Total (After Rounding)</Typography>
                <Typography className="detailFieldvalue">{currencyCode}{GlobalFunctionHelper.amountFormatter(_billData?.[DetailsInvoiceField.TOTAL_AFTER_ADJUSTMENT] ?? 0, 2)}</Typography>
            </div>

            <div className="amountDetailsItemContainer">
                <Typography className="detailFieldHeader">Outstanding Balance</Typography>
                <Typography className="detailFieldvalue outstandingBalance" color="errorText">{currencyCode}{GlobalFunctionHelper.amountFormatter((_billData?.[DetailsInvoiceField.STATUS] == BillingStatusEnum.PAID ? 0 : (_billData?.[DetailsInvoiceField.TOTAL_AFTER_ADJUSTMENT] ?? 0)), 2)}</Typography>
            </div>
        </div>
    </InvoiceDetailsContainer>
}
